<template>
  <div>
    <transition name="modal">
      <confirmation-modal
        v-if="showClearModal"
        :message="clearConfirmMessage"
        @close="showClearModal = false"
        @confirm="confirmClear"
      />
    </transition>

    <div v-if="!loading">
      <h2>Notificaciones</h2>

      <div class="mt-4 flex flex-wrap gap-2">
        <button
          v-for="cat in categoryTabs"
          :key="cat.value || 'all'"
          type="button"
          class="category-chip"
          :class="[selectedCategory === cat.value ? 'category-chip--active' : '']"
          @click="selectCategory(cat.value)"
        >
          <span>{{ cat.label }}</span>
          <span
            v-if="cat.unread > 0"
            class="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold"
            :class="selectedCategory === cat.value ? 'bg-white text-primary-600' : 'bg-error-500 text-white'"
          >
            {{ cat.unread }}
          </span>
        </button>
      </div>

      <div v-if="notifications.length === 0" class="mt-6 text-content-400">
        No existen notificaciones en esta categoría.
      </div>

      <div v-else class="mt-4 flex flex-wrap gap-2">
        <button
          class="secondary-button"
          :disabled="actionLoading"
          @click.prevent="confirmAllNotifications"
        >
          Marcar todas como leídas
        </button>

        <button
          class="delete-button"
          :disabled="actionLoading"
          @click.prevent="openClearModal"
        >
          Borrar leídas
        </button>
      </div>

      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="card w-full my-2"
      >
        <div class="flex justify-between items-start gap-2">
          <div class="flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="category-tag" :class="`category-tag--${notification.category || 'system'}`">
                {{ categoryLabel(notification.category) }}
              </span>
              <span
                v-if="notification.status === 'pending'"
                class="text-xs font-semibold text-error-500"
              >
                Nueva
              </span>
            </div>

            <p
              class="text-sm mt-2"
              :class="{ 'font-semibold': notification.status === 'pending' }"
            >
              {{ notification.message }}
            </p>

            <p class="text-xs text-content-400 mt-1">
              {{ formatDatetime(notification.created_at) }}
            </p>
          </div>

          <button
            class="text-content-400 hover:text-error-500 p-1 leading-none"
            title="Eliminar notificación"
            :disabled="actionLoading"
            @click.prevent="destroyNotification(notification.id)"
          >
            ✕
          </button>
        </div>

        <div class="mt-2 flex flex-wrap gap-2">
          <button
            v-if="notification.status === 'pending'"
            class="secondary-button text-sm"
            @click="confirmNotification(notification.id, notification.redirect_url)"
          >
            {{ notification.redirect_url ? "Confirmar e ir" : "Confirmar" }}
          </button>

          <a
            v-if="notification.redirect_url && notification.status === 'confirmed'"
            class="secondary-button text-sm"
            :href="notification.redirect_url"
          >
            Ir
          </a>
        </div>
      </div>

      <div v-if="olderCount > 0" class="mt-4 text-center">
        <button
          class="secondary-button"
          :disabled="loadingMore"
          @click.prevent="loadMore"
        >
          {{ loadingMore ? "Cargando..." : `Cargar más (+${olderCount})` }}
        </button>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { formatDatetime } from "@/utils/dateFormatter";

const CATEGORY_META = {
  payment: { label: "Pago" },
  order: { label: "Órdenes" },
  plan: { label: "Planes" },
  pet: { label: "Mascotas" },
  inventory: { label: "Inventario" },
  system: { label: "Sistema" },
};

export default {
  name: "Notifications",

  components: { ConfirmationModal },

  data() {
    return {
      notifications: [],
      olderCount: 0,
      unreadByCategory: {},
      loading: true,
      loadingMore: false,
      actionLoading: false,
      selectedCategory: null,
      showClearModal: false,
    };
  },

  computed: {
    categoryTabs() {
      const totalUnread = Object.values(this.unreadByCategory).reduce(
        (a, b) => a + Number(b || 0),
        0
      );
      const tabs = [{ value: null, label: "Todas", unread: totalUnread }];
      Object.keys(CATEGORY_META).forEach((key) => {
        tabs.push({
          value: key,
          label: CATEGORY_META[key].label,
          unread: Number(this.unreadByCategory[key] || 0),
        });
      });
      return tabs;
    },

    clearConfirmMessage() {
      const scope = this.selectedCategory
        ? `de la categoría "${CATEGORY_META[this.selectedCategory].label}"`
        : "leídas";
      return `¿Borrar las notificaciones ${scope}?`;
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "notifications");
    this.$store.dispatch("loadUser");
    this.loadNotifications();
  },

  methods: {
    formatDatetime,

    categoryLabel(cat) {
      return (CATEGORY_META[cat] || CATEGORY_META.system).label;
    },

    selectCategory(cat) {
      if (this.selectedCategory === cat) return;
      this.selectedCategory = cat;
      this.loadNotifications();
    },

    async loadNotifications() {
      this.loading = true;
      try {
        const response = await api.notifications.getAll(this.selectedCategory);
        this.notifications = response.data || [];
        this.olderCount = response.older_count || 0;
        this.unreadByCategory = response.unread_by_category || {};
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.loading = false;
    },

    async loadMore() {
      if (this.loadingMore || this.notifications.length === 0) return;
      this.loadingMore = true;
      try {
        const lastId = this.notifications[this.notifications.length - 1].id;
        const response = await api.notifications.getAll(
          this.selectedCategory,
          lastId
        );
        this.notifications = this.notifications.concat(response.data || []);
        this.olderCount = response.older_count || 0;
        this.unreadByCategory = response.unread_by_category || {};
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.loadingMore = false;
    },

    async confirmNotification(id, redirect_url) {
      this.actionLoading = true;
      try {
        await api.notifications.confirm(id);
        if (redirect_url) {
          window.location.href = redirect_url;
          return;
        }
        await this.$store.dispatch("loadUser");
        await this.loadNotifications();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.actionLoading = false;
    },

    async confirmAllNotifications() {
      this.actionLoading = true;
      try {
        await api.notifications.confirmAll();
        await this.$store.dispatch("loadUser");
        await this.loadNotifications();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.actionLoading = false;
    },

    async destroyNotification(id) {
      this.actionLoading = true;
      try {
        await api.notifications.destroy(id);
        await this.loadNotifications();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.actionLoading = false;
    },

    openClearModal() {
      this.showClearModal = true;
    },

    async confirmClear() {
      this.showClearModal = false;
      this.actionLoading = true;
      try {
        const result = await api.notifications.clear({
          category: this.selectedCategory,
          scope: "read",
        });
        this.$store.dispatch(
          "successSnack",
          result.message || "Notificaciones limpiadas"
        );
        await this.loadNotifications();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.actionLoading = false;
    },
  },
};
</script>

<style scoped>
.category-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: white;
  color: #4b5563;
  transition: all 0.15s;
}
.category-chip:hover {
  border-color: #c4b5fd;
}
.category-chip--active {
  background-color: #5e3a9a;
  border-color: #5e3a9a;
  color: white;
}
.category-tag {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.category-tag--payment { background-color: #fef3c7; color: #92400e; }
.category-tag--order { background-color: #dbeafe; color: #1e40af; }
.category-tag--plan { background-color: #ede9fe; color: #5b21b6; }
.category-tag--pet { background-color: #dcfce7; color: #166534; }
.category-tag--inventory { background-color: #ffedd5; color: #9a3412; }
.category-tag--system { background-color: #f3f4f6; color: #374151; }
</style>
